import {CustomSVG} from './CustomSVG'
import {CustomFileData} from '@/shared/utils/crud-helper/models'
import type {FileTags} from '@/shared/utils/ontology/tags'
import {useOntologyTree} from '@/shared/utils/hooks/useOntologyTree'

const iconStyle: React.CSSProperties = {marginLeft: '2px', fontSize: 'var(--ob-font-size-base)'}

/** The folder glyph of a file row; every folder (shared or not) shows this one. */
export const FolderIcon: React.FC = () => <i className='fas fa-folder' style={iconStyle}></i>

interface props {
  customFile: CustomFileData
}

/** The icon of a file-manager row. */
export const FileIcon: React.FC<props> = ({customFile}) => (
  <FileTypeIcon name={customFile.name} tags={customFile.tags} isDir={customFile.isDir} />
)

export interface FileTypeIconProps {
  /** File name (or path); its extension picks the fallback icon. */
  name: string
  /** The file's ontology tags, which pick the per-datatype icon when the tree knows them. */
  tags?: FileTags | null
  isDir?: boolean
}

/**
 * The icon for a file by its type, shared by the file manager and the result viewer tabs so a file
 * looks the same wherever it appears.
 */
export const FileTypeIcon: React.FC<FileTypeIconProps> = ({name, tags, isDir = false}) => {
  const {resolver} = useOntologyTree()

  // Ontology-driven per-datatype icon (from the file's ontology term), mirroring
  // how the desktop "Cloud Files" panel derives an icon from the object class.
  // Rendered as a plain <img> from the CDN (img-src only — no fetch/CORS, unlike
  // the inlined CustomSVG). Falls through to the extension-based icons below when
  // unavailable, so files without tags (or before the tree loads) look unchanged.
  if (!isDir) {
    const ontologyIconUrl = resolver.iconUrl(tags)
    if (ontologyIconUrl) {
      return (
        <span className='svg-icon svg-icon-6'>
          <img src={ontologyIconUrl} alt='' className='mh-50px' width={16} height={16} />
        </span>
      )
    }
  }

  const folder = 'fas fa-folder'
  const image = 'fas fa-file-image'
  const file = 'fas fa-file-alt'
  const zip = 'fas fa-file-archive'
  let icon: string = file
  let fileExt = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
  let isSvg =
    fileExt === 'gif' ||
    fileExt === 'jpg' ||
    fileExt === 'pdf' ||
    fileExt === 'png' ||
    fileExt === 'tif' ||
    fileExt === 'txt' ||
    fileExt === 'zip' ||
    fileExt === 'svg' ||
    fileExt === 'box'

  if (isDir) {
    icon = folder
  } else {
    if (fileExt !== '') {
      if (fileExt === 'jpeg' || fileExt === 'tiff') {
        icon = image
      } else if (fileExt === '7z' || fileExt === 'gz' || fileExt === 'rar') {
        icon = zip
      }
    }
  }

  if (isSvg) {
    return (
      <>
        <CustomSVG path={`/media/svg/file-extension-logos/${fileExt}.svg`} className='svg-icon-6' />
      </>
    )
  } else {
    return (
      <>
        <i className={`${icon}`} style={iconStyle}></i>
      </>
    )
  }
}
